import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../webmodule-common/other/ui/modal-confirmation';
import { customElement } from 'lit/decorators.js';
import { EventGetReference, EventSnippet } from '../../../webmodule-common/other/ui/events';
import { FabricationJobState, FabricationJobSummaryView } from '../../api/fabrication-job-api';
import { getApiFactory } from '../../api/api-injector';
import { getFabricationJobStatus } from '../data/fabrication-job-helper-functions';
import { LangFabricationJobSummaryTable } from '../../../localization/scripts/fabrication-jobs/views/fabrication-job-summary-table-lang';
import { goURL, resolveURL } from '../../../webmodule-common/other/ui/resource-resolver';
import { html, TemplateResult } from 'lit';
import { IStaleData } from '../../../webmodule-common/other/stale-data';
import { LitTableWrapper } from '../../../webmodule-common/other/ui/littable-view';
import { staleIcon } from '../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { RequestPage, ResultPaginated } from '../../../webmodule-common/other/ui/RequestPage';
import { resourceFabricationJob } from '../ui/launcher';
import {
  WebModuleLitTable,
  WebModuleLitTableColumnDef,
  WebmoduleSelectEvent
} from '../../../webmodule-common/components/src/webmodule-components';

export type TableEventFabricationJob = (job: FabricationJobSummaryView) => Promise<void>;

export interface FabricationJobSummaryTableOptions {
  states: FabricationJobState[];
  title: EventSnippet;
  branchId: EventGetReference;
  pageFragment: string;
  deleteJobEvent?: TableEventFabricationJob;
  stale?: IStaleData;
}

@customElement('wm-fabricationjobsummarytable')
export class FabricationJobSummaryTable extends LitTableWrapper<FabricationJobSummaryView> {
  eventTitle: EventSnippet;
  branchId: EventGetReference;
  states: FabricationJobState[];
  searchTerm: string | null = null;
  pageFragment: string;
  dataIsStale = false;
  deleteJobEvent?: TableEventFabricationJob;
  staleData: IStaleData;

  constructor(options: FabricationJobSummaryTableOptions) {
    super();
    this.states = options.states;
    this.eventTitle = options.title;
    this.branchId = options.branchId;
    this.pageFragment = options.pageFragment;
    this.deleteJobEvent = options.deleteJobEvent;
    this.staleData = options.stale ?? { exists: () => false };
  }

  stale(row: FabricationJobSummaryView) {
    return this.staleData.exists(row.id);
  }

  isDataLoadDelayed(): boolean {
    return true;
  }

  override enableFiltering(): boolean {
    return true;
  }

  override updateFilter(_searchTerm: string | null): void {
    this.searchTerm = _searchTerm;
  }

  async getRowsFromServer(request: RequestPage): Promise<ResultPaginated<FabricationJobSummaryView>> {
    const api = getApiFactory().fabricationJob();
    const result = await api.getFabricationJobSummary({
      branchId: await this.branchId(),
      states: this.states,
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      searchTerm: this.searchTerm ?? null
    });

    if (!result) {
      return {
        count: 0,
        pageCount: 0,
        pageIndex: 0,
        pageSize: this.pageLength(),
        results: []
      };
    }

    return result;
  }

  getColumns(): WebModuleLitTableColumnDef[] {
    return [
      {
        title: () => LangFabricationJobSummaryTable.jobNumberTitle(),
        fieldName: 'jobNumber',
        sortable: true,
        classes: 'colpx-70 fabrication-job-number',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return this.stale(job)
            ? // eslint-disable-next-line @typescript-eslint/no-base-to-string
              html`${staleIcon()}${this.getJobLink(job, job.jobNumber.toString())}`
            : this.getJobLink(job, job.jobNumber.toString());
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.titleTitle(),
        fieldName: 'title',
        sortable: true,
        classes: 'colpx-150 fabrication-job-title',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return this.getJobLink(job, job.title || LangFabricationJobSummaryTable.untitled());
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.statusTitle(),
        fieldName: 'state',
        classes: 'colpx-90 fabrication-job-state no-pseudo',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return getFabricationJobStatus(job.state, true);
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.itemsTitle(),
        fieldName: 'itemCount',
        classes: 'colpx-50 fabrication-job-items no-pseudo',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return html`${job.itemCount}`;
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.createdTitle(),
        fieldName: 'dateCreated',
        sortable: true,
        classes: 'colpx-90 fabrication-job-created no-pseudo',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          const dt = new Date(job.dateCreated);
          return html`${dt.toLocaleDateString()}`;
        }
      },
      {
        title: () => LangFabricationJobSummaryTable.modifiedTitle(),
        fieldName: 'lastModifiedDate',
        sortable: true,
        classes: 'colpx-90 fabrication-job-modified no-pseudo',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          if (!job.lastModifiedDate) return html``;
          const dt = new Date(job.lastModifiedDate);
          return html`${dt.toLocaleDateString()}`;
        }
      },
      {
        title: () => '',
        classes: 'colpxmax-50 no-pseudo',
        fieldName: 'xx',
        displayValue: (_table: WebModuleLitTable, item: unknown) => {
          const job = item as FabricationJobSummaryView;
          return this.ellipsisMenu(job);
        }
      }
    ];
  }

  getDefaultSortAsc(): boolean {
    return false;
  }

  getDefaultSortField(): string {
    return 'jobNumber';
  }

  private jobLinkClicked(e: MouseEvent, jobId: string) {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    goURL(resourceFabricationJob, jobId);
  }

  protected getJobLink(row: FabricationJobSummaryView, value: string): TemplateResult {
    return html`<a
      class="fabrication-job-link"
      href="${resolveURL(resourceFabricationJob, row.id)}"
      data-fabricationjobid="${row.id}"
      @click=${(e: MouseEvent) => this.jobLinkClicked(e, row.id)}
      >${value}</a
    >`;
  }

  private async onMenuItemSelected(event: WebmoduleSelectEvent, job: FabricationJobSummaryView) {
    if (event.detail.item.value === 'delete') {
      if (
        await AskConfirmation(
          LangFabricationJobSummaryTable.deleteConfirmation(),
          getConfirmationButtons()[ConfirmationButtonType.yesNo]
        )
      ) {
        await this.deleteJobEvent?.(job);
      }
    }
  }

  private ellipsisMenu(job: FabricationJobSummaryView): TemplateResult {
    if (job.state !== FabricationJobState.Draft) return html``;

    return html`<webmodule-dropdown
      placement="bottom-end"
      hoist
      @webmodule-select=${(e: WebmoduleSelectEvent) => this.onMenuItemSelected(e, job)}
    >
      <webmodule-icon-button slot="trigger" library="fa" name="fas-bars"></webmodule-icon-button>
      <webmodule-menu>
        <webmodule-menu-item value="delete">
          <webmodule-icon slot="prefix" library="fa" name="fas-trash"></webmodule-icon>
          ${LangFabricationJobSummaryTable.deleteMenuItem()}
        </webmodule-menu-item>
      </webmodule-menu>
    </webmodule-dropdown>`;
  }
}
